import React, { useEffect, useState } from "react";
import { bootClarity, applyClarityConsent, readConsentRecord } from "../../lib/clarity";

/**
 * Analytics loader — single source of truth for GA4 + Ads consent + Clarity.
 *
 * Property routing (Feb 2026)
 * ---------------------------
 * * GA4 property 482654511 · web stream 10409081386 · measurement id
 *   `G-KVNPTG545S` (Medical Peptide Store). Loaded HERE via one
 *   `gtag/js?id=G-KVNPTG545S` <script> tag; there is intentionally NO
 *   Google Tag Manager container installed on this site (a prior
 *   container was removed after it mis-routed collection to a
 *   non-owned GA4 property — do not re-introduce a GTM container).
 * * Google Ads `AW-18375113165` — kept in `public/index.html` on its
 *   original direct `gtag.js` path, along with its Consent Mode v2
 *   defaults, so the conversion tag continues to work.
 * * Microsoft Clarity `ya2b98n3bu` — loaded from `../../lib/clarity.js`
 *   with denied-by-default Consent v2 (case-sensitive keys).
 *
 * Consent flow (unchanged from prior release)
 * -------------------------------------------
 * 1. `public/index.html` emits `gtag('consent','default',{... denied ...})`
 *    BEFORE any tag script — that primes the shared `window.dataLayer`.
 * 2. This component boots GA4 + Clarity ONCE on mount. Because both
 *    scripts inherit the primed denied defaults, no collection cookie
 *    can be set before the shopper's decision.
 * 3. When the shopper picks "Accept analytics" or "Essential only",
 *    `CookieConsent.jsx` writes `mps_cookie_consent_v1` and dispatches
 *    the `mps:consent-updated` DOM event. This component listens for
 *    that event and mirrors the decision into two channels:
 *      a. `gtag('consent','update',{ad_storage, analytics_storage, ...})`
 *      b. `clarity('consent','v2',{ad_Storage, analytics_Storage})`
 *    No reload needed.
 *
 * Invariants
 * ----------
 * * Exactly ONE GA4 loader script tag is inserted (`id="mps-ga4-loader"`
 *   guard). Idempotent across remounts.
 * * A previously mis-routed GA4 measurement id (a container we did not
 *   own) must never resurface anywhere in this file after refactor.
 */

export const GA4_MEASUREMENT_ID = "G-KVNPTG545S";
const GA4_LOADER_SCRIPT_ID = "mps-ga4-loader";

/**
 * Idempotently inject the GA4 gtag.js loader for `GA4_MEASUREMENT_ID`
 * and register the config so pageviews route to the correct property.
 * Callable from tests via the `{document, window}` overrides.
 */
export function bootGA4({ document: doc, window: win } = {}) {
  const d = doc || (typeof document !== "undefined" ? document : null);
  const w = win || (typeof window !== "undefined" ? window : null);
  if (!d || !w) return { booted: false, reason: "no_window" };
  if (d.getElementById(GA4_LOADER_SCRIPT_ID)) return { booted: false, reason: "already_booted" };

  // Ensure the gtag() shim exists and shares `window.dataLayer` with the
  // Consent Mode default block in index.html + the Google Ads tag.
  w.dataLayer = w.dataLayer || [];
  if (typeof w.gtag !== "function") {
    w.gtag = function () { w.dataLayer.push(arguments); };
  }

  const s = d.createElement("script");
  s.async = true;
  s.id = GA4_LOADER_SCRIPT_ID;
  s.src = "https://www.googletagmanager.com/gtag/js?id=" + encodeURIComponent(GA4_MEASUREMENT_ID);
  const first = d.getElementsByTagName("script")[0];
  if (first && first.parentNode) first.parentNode.insertBefore(s, first);
  else d.head.appendChild(s);

  // Register the property. Analytics collection stays inhibited by the
  // Consent Mode v2 `analytics_storage: 'denied'` default until the
  // shopper accepts analytics — no config-time flag disables it.
  w.gtag("js", new Date());
  w.gtag("config", GA4_MEASUREMENT_ID);
  return { booted: true, reason: "ok" };
}

export const AnalyticsLoader = () => {
  // Bumped whenever the shopper accepts / rejects — forces the effect
  // below to re-read the consent record and re-emit the update signal.
  const [consentTick, setConsentTick] = useState(0);

  useEffect(() => {
    const handler = () => setConsentTick((n) => n + 1);
    window.addEventListener("mps:consent-updated", handler);
    return () => window.removeEventListener("mps:consent-updated", handler);
  }, []);

  // Boot GA4 + Clarity exactly once on mount. Both scripts inherit the
  // denied Consent Mode defaults that index.html primes.
  useEffect(() => {
    bootGA4();
    bootClarity();
    // If the shopper's decision was already stored from a prior visit,
    // release the appropriate signals immediately so heatmaps + GA4
    // resume without waiting for the banner to reappear.
    const stored = readConsentRecord();
    if (stored) applyClarityConsent(stored);
  }, []);

  useEffect(() => {
    const consent = readConsentRecord();
    if (!consent) return;

    // Shared dataLayer / gtag shim (index.html already primed the array).
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = window.gtag || gtag;

    const granted = "granted";
    const denied  = "denied";
    // 1) Google Consent Mode v2 → both GA4 (G-KVNPTG545S) and Google
    //    Ads (AW-18375113165) honour these signals.
    window.gtag("consent", "update", {
      ad_storage:         consent.marketing ? granted : denied,
      analytics_storage:  consent.analytics ? granted : denied,
      ad_user_data:       consent.marketing ? granted : denied,
      ad_personalization: consent.marketing ? granted : denied,
    });
    // 2) Microsoft Clarity Consent v2 (case-sensitive keys — do not
    //    "correct" them).
    applyClarityConsent(consent);
  }, [consentTick]);

  return null;
};

// Test helper — reset the module-level guards so unit tests can boot
// again after simulating a fresh document. Not part of the public API.
export function __resetGA4ForTests() {
  if (typeof document === "undefined") return;
  const s = document.getElementById(GA4_LOADER_SCRIPT_ID);
  if (s && s.parentNode) s.parentNode.removeChild(s);
  if (typeof window !== "undefined") {
    delete window.gtag;
    window.dataLayer = [];
  }
}
